import React, {useState, useEffect} from "react";
import LineGraph from "./LineGraph";
import BarChart from "./BarChart";
import API from "../../helpers/apiHelpers";
import { makeStyles } from "@material-ui/core/styles";
import {Grid,
    Container,
    Box,
    Card,
    Typography,
    CircularProgress,
    TextField,
    useTheme,
    hslToRgb, useMediaQuery} from "@material-ui/core";
import { Autocomplete, Rating, ToggleButton, ToggleButtonGroup } from "@material-ui/lab";
import { DatePicker } from "@material-ui/pickers";
import Navigation from "../../Components/Navigation";
import CountUp from "react-countup";
import moment from "moment";
import {timezones} from "../../Constants/timezones";
import {hourlyBreakdownConstant, dailyBreakdown, timezonesWithOffSet} from "../../Constants/partnerDashboardConstants";
import useWindowSize from "../../helpers/windowResizeHook";
var _ = require("lodash");

const timePeriods = [
    {id: "LAST_WEEK", name: "Last 7 Days"},
    {id: "LAST_FOUR_WEEKS", name: "Last 4 Weeks"},
    {id: "LAST_THREE_MONTH", name: "Last 3 Months"},
    {id: "LAST_TWELVE_MONTH", name: "Last 12 Months"},
    {id: "ALL_TIME", name: "All Time"},
    {id: "CUSTOM", name: "Custom"}
];

const timeBuckets = ["Daily", "Weekly", "Monthly"];

const countStyles = makeStyles((theme) => ({
    positive: {
        color: "#45CD53",
    },
    negative: {
        color: "#CD5345",
    },
    zero: {
        color: "#000000",
    },
}));

const PartnerDashboard = () => {
    const [timeFrame, setTimeFrame] = useState<any>(timePeriods[0]);
    const [data, setData] = useState<undefined | any>();
    const [startDate, setStartDate] = useState<string>(moment().subtract(7, "days").format("YYYY-MM-DD"));
    const [endDate, setEndDate] = useState<string>(moment().format("YYYY-MM-DD"));
    const [timeBucket, setTimeBucket] = useState<string>("Daily");
    const [timeZone, setTimezone] = useState<any>(timezonesWithOffSet[0]);
    const [partnerId, setPartnerId] = useState<undefined | number>();
    const windowSize = useWindowSize();
    const countUpStyles = countStyles();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const useStyles = makeStyles((theme) => ({
        heading: {
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            marginBottom: 20,
        },
        ratings: {
            display: "flex",
            flexDirection: "row",
            justifyContent: "flexStart",
            alignItems: "center",
            paddingTop: 30
        },
        item1: {
            order: 2,
            [theme.breakpoints.up("sm")]: {
                order: 1,
            },
        },
        item2: {
            order: 1,
            [theme.breakpoints.up("sm")]: {
                order: 2,
            },
        },
        item3:{
            order: 3,
            width: windowSize.width > 684 ? "684px" : windowSize.width > 599 ? windowSize.width-70 : windowSize.width-20,
            height: "300px",
        },
        largeChart: {
            width: windowSize.width > 684 ? "684px" : windowSize.width > 599 ? windowSize.width-70 : windowSize.width-20, 
            height: "300px",
            paddingBottom: 20,
        },
        smallChart: {
            width: windowSize.width > 684 ? `${(684/3)-20}px`: windowSize.width > 599 ? windowSize.width/3-40 : windowSize.width-100,
            height: "100px",
            paddingBottom: 20,
        }
    }));

    const styles = useStyles();

    const getPartner = async () => {
        const resp = await API.getPartners();
        const partner = resp.filter(item => item.creatorId === API.userId);
        if (partner.length > 0){
            setPartnerId(partner[0].id);
        }else {
            setPartnerId(0);
        }
    };

    const getMetrics = async (timeFrame) => {
        try{
            const params = API.role === "PARTNER" ? {
                partnerId: partnerId,
                range: timeFrame.id,
                rangeBegins: startDate,
                rangeEnds: endDate
            } : {
                range: timeFrame.id,
                rangeBegins: startDate,
                rangeEnds: endDate
            };
            const results = await API.getPartnerStatistics(params);
            setData(results);

        }catch (err) {
            console.error(err);
        }
    };

    const convertAppointmentData = (type) => {
        let d = data.appointmentStatisticDetails.filter(item => item.type === type)[0].daily;
        const dataObj = [{
            id: type,
            color: "hsl(181, 52%, 60%)",
            data: []
        }];

        const timeData = [];
        if (timeBucket === "Weekly"){
            let groupedResults = _.groupBy(d, (result) => moment(result["time"], "YYYY-MM-DD").startOf("week"));
            Object.keys(groupedResults).map(key => {
                let count = 0;
                groupedResults[key].map(item => {
                    count = count + item.count;
                });
                timeData.push({
                    x: moment(key),
                    y: count
                });
            });
        }else if (timeBucket === "Monthly"){
            let groupedResults = _.groupBy(d, (result) => moment(result["time"], "YYYY-MM-DD").startOf("month"));
            Object.keys(groupedResults).map(key => {
                let count = 0;
                groupedResults[key].map(item => {
                    count = count + item.count;
                });
                timeData.push({
                    x: moment(key),
                    y: count
                });
            });
        }else{
            d.map(point => {
                if (moment(point.time).format("YYYY-MM-DD") !== "Invalid date"){	
                    timeData.push({
                        x: moment(point.time),
                        y: point.count
                    });
                }
            });
        }
			
        timeData.sort((a,b) => {
            return a.x - b.x;
        });
		
        dataObj[0].data = timeData.map(item => {
            return {
                x: item.x.format("YYYY-MM-DD"),
                y: item.y
            };
        });

        // if (timeBucket !== "Daily"){
        // 	dataObj[0].data[0].x = startDate;
        // 	dataObj[0].data[timeData.length - 1] = endDate
        // }
        // console.log(dataObj[0])

        return dataObj;
    };

    const cumulativeData = () => {
        let dataObj=[];
        dailyBreakdown.map(item => {
            let val = data.weeklyBreakdown.filter(row => row.day === item.day);
            dataObj.push({
                day: item.day.substring(0,3),
                order: item.order,
                value: val.length > 0 ? val[0].value : 0
            });
        });
        return dataObj.sort((a,b) => {return a.order - b.order;});
    };

    const hourlyData = () => {
        let dataObj = [{
            id: "hourlyBreakdown",
            color: "hsl(181, 52%, 60%)",
            data: []
        }];

        let offset = timeZone.offset;
        hourlyBreakdownConstant.map(item => {
            let val = data.hourlyBreakdown.filter(row => row.time === item.time);
            dataObj[0].data.push({
                x: offset !== 0 ? (item.order > offset ? item.order - offset : (24+item.order) - offset) : item.order,
                y: val.length > 0 ? val[0].value : 0,
            });
        });

        dataObj[0].data.sort((a,b) => {
            return a.x - b.x;
        });
		
        return dataObj;
    };

    const getDifference = (type) => {
        return data.appointmentStatisticDetails.filter(item => item.type === type)[0].difference;
    };

    const timeBucketParse = () => {
        if (timeBucket === "Daily"){
            return "day";
        }else if (timeBucket === "Weekly"){
            return "week";
        }else {
            return "month";
        }
    };

    useEffect(() => {
        if (!partnerId){
            getPartner();
        }
    }, [partnerId]);

    useEffect(() => {
        if (!data && partnerId !== undefined) {
            getMetrics(timeFrame);
        }
    }, [data]);

    useEffect(() => {
        if (partnerId !== undefined){
            getMetrics(timeFrame);
        }
    }, [startDate, endDate, partnerId]);

    // useEffect(() => {
    // 	console.log(timeZone)
    // }, [timeZone]);

    return (
        <Container maxWidth="md">
            <Box marginTop="115px" marginBottom="75px">
                <Grid container style={{paddingBottom: 40}}>
                    {!isMobile && 
						<Grid item md={3} xs={12}>
						    <Navigation />
						</Grid>
                    }
                    <Grid item md={9} xs={12}>
                        <Card variant="outlined" style={{padding: 36}}>
                            {data ? (
                                <>
                                    <Grid container className={styles.heading}>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="h2" style={{marginBottom: 20}}>
                                                Appointments
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Autocomplete
                                                id="timeFrame"
                                                disableClearable={true}
                                                value={timeFrame}
                                                onChange={(event, value) => {setTimeFrame(value); if (value.id !== "CUSTOM"){getMetrics(value);}}}
                                                // onChange={(event, value) => {setTimeFrame(value)}}
                                                options={timePeriods}
                                                style={{marginBottom: 20}}
                                                getOptionLabel={(option) => option.name}
                                                renderInput={(params) => <TextField {...params} label="Time Period" variant="outlined" />}                                   
                                            />
                                        </Grid>
                                        {timeFrame.id === "CUSTOM" &&
                                            <Grid container>
                                            	<Grid item xs={6} sm={6} style={{paddingTop: 15}}>
                                            		<DatePicker
                                            			disableFuture
                                            			openTo="date"
                                            			style={{width: 115}}
                                            			format="MM/DD/yyyy"
                                            			label="Start"
                                            			views={["year", "month", "date"]}
                                            			value={moment(startDate)}
                                            			onChange={date => setStartDate(moment(date).format("YYYY-MM-DD"))}
                                            		/>
                                            	</Grid>
                                            	<Grid item xs={6} sm={6} style={{paddingTop: 15}}>
                                            		<DatePicker
                                            			disableFuture
                                            			openTo="date"
                                            			style={{width: 115}}
                                            			format="MM/DD/yyyy"
                                            			label="End"
                                            			views={["year", "month", "date"]}
                                            			value={moment(endDate)}
                                            			onChange={date => setEndDate(moment(date).format("YYYY-MM-DD"))}
                                            		/>
                                            	</Grid>
                                            </Grid>
                                        }
                                    </Grid>
                                    <Grid container>
                                        <Grid item xs={12} sm={8} className={styles.item1}>
                                            <Typography variant="h5" style={{paddingBottom: 20, paddingTop: 10}}>
                                                <span style={{paddingRight: 10}}> Appointment Growth</span>
                                                {timeFrame.id !== "ALL_TIME" &&
													<CountUp 
													    end={getDifference("APPOINTMENTS")}
													    suffix="%"
													    className={getDifference("APPOINTMENTS") > 0 ? countUpStyles.positive : getDifference("APPOINTMENTS") != 0 ? countUpStyles.negative : countUpStyles.zero}
													/>
                                                }
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={4} className={styles.item2}>
                                            <ToggleButtonGroup
                                                value={timeBucket}
                                                exclusive
                                                size="small"
                                                onChange={(e,value) => setTimeBucket(value)}
                                            >
                                                {timeFrame.id !== "LAST_WEEK" &&
													<ToggleButton value="Daily">
														Daily
													</ToggleButton>
                                                }
                                                {timeFrame.id !== "LAST_WEEK" &&
													<ToggleButton value="Weekly">
														Weekly
													</ToggleButton>
                                                }
                                                {timeFrame.id !== "LAST_WEEK" && timeFrame.id !== "LAST_FOUR_WEEKS" && 
													<ToggleButton value="Monthly">
														Monthly
													</ToggleButton>
                                                }
                                            </ToggleButtonGroup>
                                        </Grid>
                                        <div className={styles.item3}>
                                            <LineGraph data={convertAppointmentData("APPOINTMENTS")} type={timeBucketParse()} showDate={true}/>
                                        </div>
                                    </Grid>
                                    <Grid container>
                                        <Grid item xs={12}>
                                            <Typography variant="h5"  style={{paddingBottom: 20, paddingTop: 40}}>
                                                Breakdown by Appointment Type
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <Typography align="center" style={{color: "#000"}}>
                                                <span style={{paddingRight: 10, fontSize: "14px"}}>Chat</span>
                                                {timeFrame.id !== "ALL_TIME" &&
													<CountUp 
													    end={getDifference("CHAT_APPOINTMENTS")}
													    suffix="%"
													    className={getDifference("CHAT_APPOINTMENTS") > 0 ? countUpStyles.positive : getDifference("CHAT_APPOINTMENTS") != 0 ? countUpStyles.negative : countUpStyles.zero}
													/>
                                                }
                                            </Typography>
                                            <div className={styles.smallChart}>
                                                <LineGraph data={convertAppointmentData("CHAT_APPOINTMENTS")} type={timeBucketParse()} showDate={false}/>
                                            </div>
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <Typography  align="center" style={{color: "#000"}}>
                                                <span style={{paddingRight: 10, fontSize: "14px"}}>Video</span>
                                                {timeFrame.id !== "ALL_TIME" &&
													<CountUp 
													    end={getDifference("VIDEO_APPOINTMENTS")}
													    suffix="%"
													    className={getDifference("VIDEO_APPOINTMENTS") > 0 ? countUpStyles.positive : getDifference("VIDEO_APPOINTMENTS") != 0 ? countUpStyles.negative : countUpStyles.zero}
													/>
                                                }
                                            </Typography>
                                            <div className={styles.smallChart}>
                                                <LineGraph data={convertAppointmentData("VIDEO_APPOINTMENTS")} type={timeBucketParse()} showDate={false}/>
                                            </div>
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <Typography align="center" style={{color: "#000"}}>
                                                <span style={{paddingRight: 10, fontSize: "14px"}}>Repeat</span>
                                                {timeFrame.id !== "ALL_TIME" &&
													<CountUp 
													    end={getDifference("REPEAT_APPOINTMENTS")}
													    suffix="%"
													    className={getDifference("REPEAT_APPOINTMENTS") > 0 ? countUpStyles.positive : getDifference("REPEAT_APPOINTMENTS") != 0 ? countUpStyles.negative : countUpStyles.zero}
													/>
                                                }
                                            </Typography>
                                            <div className={styles.smallChart}>
                                                <LineGraph data={convertAppointmentData("REPEAT_APPOINTMENTS")} type={timeBucketParse()} showDate={false}/>
                                            </div>
                                        </Grid>
                                    </Grid>
                                    <Grid container>
                                        <Grid item xs={12}>
                                            <Typography variant="h5"  style={{paddingBottom: 20, paddingTop: 10}}>
                                                Breakdown by Animal Type
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <Typography align="center" style={{color: "#000"}}>
                                                <span style={{paddingRight: 10, fontSize: "14px"}}>Cats</span>
                                                {timeFrame.id !== "ALL_TIME" &&
													<CountUp 
													    end={getDifference("CATS_APPOINTMENTS")}
													    suffix="%"
													    className={getDifference("CATS_APPOINTMENTS") > 0 ? countUpStyles.positive : getDifference("CATS_APPOINTMENTS") != 0 ? countUpStyles.negative : countUpStyles.zero}
													/>
                                                }
                                            </Typography>
                                            <div className={styles.smallChart}>
                                                <LineGraph data={convertAppointmentData("CATS_APPOINTMENTS")} type={timeBucketParse()} showDate={false}/>
                                            </div>
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <Typography align="center" style={{color: "#000"}}>
                                                <span style={{paddingRight: 10, fontSize: "14px"}}>Dogs</span>
                                                {timeFrame.id !== "ALL_TIME" &&
													<CountUp 
													    end={getDifference("DOGS_APPOINTMENTS")}
													    suffix="%"
													    className={getDifference("DOGS_APPOINTMENTS") > 0 ? countUpStyles.positive : getDifference("DOGS_APPOINTMENTS") != 0 ? countUpStyles.negative : countUpStyles.zero}
													/>
                                                }
                                            </Typography>
                                            <div className={styles.smallChart}>
                                                <LineGraph data={convertAppointmentData("DOGS_APPOINTMENTS")} type={timeBucketParse()} showDate={false}/>
                                            </div>
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <Typography align="center" style={{color: "#000"}}>
                                                <span style={{paddingRight: 10, fontSize: "14px"}}>Birds</span>
                                                {timeFrame.id !== "ALL_TIME" &&
													<CountUp 
													    end={getDifference("BIRDS_APPOINTMENTS")}
													    suffix="%"
													    className={getDifference("BIRDS_APPOINTMENTS") > 0 ? countUpStyles.positive : getDifference("BIRDS_APPOINTMENTS") != 0 ? countUpStyles.negative : countUpStyles.zero}    
													/>
                                                }
                                            </Typography>
                                            <div className={styles.smallChart}>
                                                <LineGraph data={convertAppointmentData("BIRDS_APPOINTMENTS")} type={timeBucketParse()} showDate={false}/>
                                            </div>
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <Typography align="center" style={{color: "#000"}}>
                                                <span style={{paddingRight: 10, fontSize: "14px"}}>Reptiles</span>
                                                {timeFrame.id !== "ALL_TIME" &&
													<CountUp 
													    end={getDifference("REPTILES_APPOINTMENTS")}
													    suffix="%"
													    className={getDifference("REPTILES_APPOINTMENTS") > 0 ? countUpStyles.positive : getDifference("REPTILES_APPOINTMENTS") != 0 ? countUpStyles.negative : countUpStyles.zero}
													/>
                                                }
                                            </Typography>
                                            <div className={styles.smallChart}>
                                                <LineGraph data={convertAppointmentData("REPTILES_APPOINTMENTS")} type={timeBucketParse()} showDate={false}/>
                                            </div>
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <Typography align="center" style={{color: "#000"}}>
                                                <span style={{paddingRight: 10, fontSize: "14px"}}>Farm Animals</span>
                                                {timeFrame.id !== "ALL_TIME" &&
													<CountUp 
													    end={getDifference("FARMS_APPOINTMENTS")}
													    suffix="%"
													    className={getDifference("FARMS_APPOINTMENTS") > 0 ? countUpStyles.positive : getDifference("FARMS_APPOINTMENTS") != 0 ? countUpStyles.negative : countUpStyles.zero}
													/>
                                                }
                                            </Typography>
                                            <div className={styles.smallChart}>
                                                <LineGraph data={convertAppointmentData("FARMS_APPOINTMENTS")} type={timeBucketParse()} showDate={false}/>
                                            </div>
                                        </Grid>
                                        <Grid item xs={12} sm={4} >
                                            <Typography align="center" style={{color: "#000"}}>
                                                <span style={{paddingRight: 10, fontSize: "14px"}}>Exotics</span>
                                                {timeFrame.id !== "ALL_TIME" &&
													<CountUp 
													    end={getDifference("EXOTIC_APPOINTMENTS")}
													    suffix="%"
													    className={getDifference("EXOTIC_APPOINTMENTS") > 0 ? countUpStyles.positive : getDifference("EXOTIC_APPOINTMENTS") != 0 ? countUpStyles.negative : countUpStyles.zero}
													/>
                                                }
                                            </Typography>
                                            <div className={styles.smallChart}>
                                                <LineGraph data={convertAppointmentData("EXOTIC_APPOINTMENTS")} type={timeBucketParse()} showDate={false}/>
                                            </div>
                                        </Grid>
                                    </Grid>
                                    <Grid container className={styles.heading}>
                                        <Grid item xs={12} sm={8}>
                                            <Typography variant="h5" style={{marginBottom: 20}}>
                                                Breakdown by Time
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <Autocomplete
                                                id="timeZone"
                                                disableClearable={true}
                                                value={timeZone}
                                                onChange={(event, value) => setTimezone(value)}
                                                options={timezonesWithOffSet}
                                                style={{marginBottom: 20}}
                                                getOptionLabel={(option) => option.zone}
                                                renderInput={(params) => <TextField {...params} label="Time Zone" variant="outlined" />}                                   
                                            />
                                        </Grid>
                                        <div className={styles.largeChart}>
                                            <LineGraph data={hourlyData()} type="cumulative" showDate={false}/>
                                        </div>
                                    </Grid>
                                    <Grid container>
                                        <Grid item xs={12}>
                                            <Typography variant="h5"  style={{paddingBottom: 20, paddingTop: 10}}>
                                                Breakdown by Day of the Week
                                            </Typography>
                                        </Grid>
                                        <div className={styles.largeChart}>
                                            <BarChart data={cumulativeData()} />
                                        </div>
                                    </Grid>
                                    <Grid container>
                                        <Grid item xs={12}>
                                            <Typography variant="h5">
                                                Average Rating
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} className={styles.ratings}>
                                            <Rating 
                                                name="size-large" 
                                                disabled={true}
                                                value={data.averageRating.currentAverageRating} 
                                                size="large" 
                                                style={{paddingRight: 10}}
                                            />
                                            <Typography style={{paddingRight: 10}}>
                                                {data.averageRating.currentAverageRating.toFixed(2)}
                                                {timeFrame.id !== "ALL_TIME" &&
													<CountUp 
													    end={Number(data.averageRating.difference.toFixed(2))} 
													    suffix="%" 
													    className={Number(data.averageRating.difference.toFixed(2)) > 0 ? countUpStyles.positive : Number(data.averageRating.difference.toFixed(2)) != 0 ? countUpStyles.negative : countUpStyles.zero}
													/>
                                                }
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </>
                            ) : (
                                <Box width="100%" height="150px" display="flex" justifyContent="center" alignItems="center">
                                    <CircularProgress />
                                </Box>
                            )}
                        </Card>
                    </Grid>
                </Grid>
            </Box>
        </Container>
    );
};

export default PartnerDashboard;